import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select from '__components/Inputs/Select';
import type {
  FlowLoadErrors,
  GenericOption,
  RunSmartConnectorErrorHandling,
  RunSmartConnectorFlowLoad,
  SmartConnector,
} from 'pages/SmartConnectors/types';
import { Section } from './Section';
import { useFlashTransition } from 'hooks/useFlashState';
import { getTranslatedOptions } from '../helpers';

const errorsOrder: Partial<
  Record<keyof RunSmartConnectorErrorHandling, number>
> = {
  on_zero_records: 0,
  if_inactive: 1,
  on_error: 2,
  on_child_cancelled: 3,
};

export const ErrorHandling: FC<{
  waitForExecution: boolean;
  errorHandling: RunSmartConnectorFlowLoad['error_handling'];
  onChange: Dispatch<SetStateAction<RunSmartConnectorFlowLoad>>;
  showError: boolean;
  errors: FlowLoadErrors['error_handling'];
  resetError: (
    patch:
      | Partial<FlowLoadErrors>
      | ((prev: FlowLoadErrors) => Partial<FlowLoadErrors>)
  ) => void;
  smartConnectorToRun?: SmartConnector;
}> = ({
  waitForExecution,
  errorHandling,
  onChange,
  showError = false,
  errors,
  resetError,
  smartConnectorToRun,
}) => {
  const { t } = useTranslation();
  const {
    onErrorOptions,
    ifInactiveOptions,
    onZeroRecordsOptions,
    onChildCancelledOptions,
  } = useMemo(() => getTranslatedOptions(t), [t]);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const firstErrorKey = useMemo(() => {
    return (
      Object.keys(errors || {}) as (keyof Exclude<
        FlowLoadErrors['error_handling'],
        undefined
      >)[]
    )
      .sort((a, b) => (errorsOrder[a] || 0) - (errorsOrder[b] || 0))
      .find((key) => errors?.[key]);
  }, [errors]);

  const errorToShow =
    showError && firstErrorKey ? errors?.[firstErrorKey] || null : null;

  const wrapperRefs = useRef<
    Record<
      keyof Exclude<FlowLoadErrors['error_handling'], undefined>,
      HTMLDivElement | null
    >
  >({
    on_zero_records: null,
    if_inactive: null,
    on_error: null,
    on_child_cancelled: null,
  });

  useEffect(() => {
    if (errorToShow) {
      flashErrorMessage(errorToShow);
      wrapperRefs.current[firstErrorKey!]?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    return () => cancelFlash();
  }, [errorToShow, flashErrorMessage, cancelFlash, firstErrorKey]);

  const validate = useMemo(
    () => ({
      message,
      showMessage,
    }),
    [message, showMessage]
  );

  const setErrorHandling = useCallback(
    (patch: Partial<RunSmartConnectorErrorHandling>) =>
      onChange((prev) => ({
        ...prev,
        error_handling: { ...prev.error_handling, ...patch },
      })),
    [onChange]
  );

  const handleChangeIfNoRecords = useCallback(
    ({
      value,
    }: GenericOption<RunSmartConnectorErrorHandling['on_zero_records']>) => {
      setErrorHandling({ on_zero_records: value });
      resetError((prev) => ({
        error_handling: { ...prev.error_handling, on_zero_records: undefined },
      }));
    },
    [setErrorHandling, resetError]
  );

  const handleChangeIfInactive = useCallback(
    ({
      value,
    }: GenericOption<RunSmartConnectorErrorHandling['if_inactive']>) => {
      setErrorHandling({ if_inactive: value });
      resetError((prev) => ({
        error_handling: { ...prev.error_handling, if_inactive: undefined },
      }));
    },
    [setErrorHandling, resetError]
  );

  const handleChangeIfError = useCallback(
    ({ value }: GenericOption<RunSmartConnectorErrorHandling['on_error']>) => {
      setErrorHandling({ on_error: value });
      resetError((prev) => ({
        error_handling: { ...prev.error_handling, on_error: undefined },
      }));
    },
    [setErrorHandling, resetError]
  );

  const handleChangeIfChildCancelled = useCallback(
    ({
      value,
    }: GenericOption<RunSmartConnectorErrorHandling['on_child_cancelled']>) => {
      setErrorHandling({ on_child_cancelled: value });
      resetError((prev) => ({
        error_handling: {
          ...prev.error_handling,
          on_child_cancelled: undefined,
        },
      }));
    },
    [setErrorHandling, resetError]
  );

  return (
    <Section title={t('Error Handling')}>
      <div ref={(el) => (wrapperRefs.current['on_zero_records'] = el)}>
        <Select
          label={t('If there are Zero Records')}
          options={onZeroRecordsOptions}
          value={errorHandling.on_zero_records}
          onChange={handleChangeIfNoRecords}
          margin
          error={!!errors?.on_zero_records}
          validate={firstErrorKey === 'on_zero_records' ? validate : undefined}
        />
      </div>
      <div ref={(el) => (wrapperRefs.current['if_inactive'] = el)}>
        <Select
          label={t('If {{name}} Is Inactive', {
            name: smartConnectorToRun?.name,
          })}
          options={ifInactiveOptions}
          value={errorHandling.if_inactive}
          onChange={handleChangeIfInactive}
          margin
          error={!!errors?.if_inactive}
          validate={firstErrorKey === 'if_inactive' ? validate : undefined}
        />
      </div>
      {waitForExecution ? (
        <>
          <div ref={(el) => (wrapperRefs.current['on_error'] = el)}>
            <Select
              label={t('If {{name}} Errors', {
                name: smartConnectorToRun?.name,
              })}
              options={onErrorOptions}
              value={errorHandling.on_error}
              onChange={handleChangeIfError}
              margin
              error={!!errors?.on_error}
              validate={firstErrorKey === 'on_error' ? validate : undefined}
            />
          </div>
          <div ref={(el) => (wrapperRefs.current['on_child_cancelled'] = el)}>
            <Select
              label={t('If {{name}} Is Cancelled', {
                name: smartConnectorToRun?.name,
              })}
              options={onChildCancelledOptions}
              value={errorHandling.on_child_cancelled}
              onChange={handleChangeIfChildCancelled}
              margin
              error={!!errors?.on_child_cancelled}
              validate={
                firstErrorKey === 'on_child_cancelled' ? validate : undefined
              }
            />
          </div>
        </>
      ) : null}
    </Section>
  );
};
